// Modal „Záruční a pozáruční servis" na detailu produktu (zadání Tomáše
// 2026-09-02): řádek „Záruka 2 roky · Poskytujeme záruční i pozáruční servis"
// pod tlačítkem do košíku je odkaz na `/servis-naradi/` a klik na něj otevírá
// modal ve stejné řeči jako „Možnosti doručení" — DESTILÁT téhle stránky:
// leadová věta, servis všech značek + specializace, co nabízíme (s cenami
// drobných úkonů), paušální ceník, proč servis u nás a kontakt na servis.
//
// Zdroj pravdy je text stránky z administrace (parsery sdílené se
// servicePage.js v serviceModel.js) — nic se nevymýšlí, když klient stránku
// upraví, změní se i modal; patička vede na celou stránku. Rám a chování
// dialogu dodává modalShell.js (sdílený s modalem dopravy).
//
// Progressive enhancement: odkaz vede na stránku sám; na `file://`, bez fetch
// a při kliku s modifikátorem (nové okno) necháváme nativní navigaci —
// stejné pravidlo jako shippingModal.js.

import { icon } from './icon.js';
import { CONTACT, reachContact } from './headerTools.js';
import { asChatTrigger } from './supportChat.js';
import { createModal, shellNode, shellClass } from './modalShell.js';
import {
  loadServicePage,
  serviceHref,
  LABEL_SPEC,
  LABEL_PRICE_PEREX,
  LABEL_BAND_TITLE,
  LABEL_BAND_TEXT,
} from './serviceModel.js';
import L from '../locale/cs/serviceModal.js';

const PREFIX = 'bs-serv';

// Texty → locale vrstva (S3 #399); dluh per soubor měří tools/lib/locale-debt.mjs
const TITLE = L.title;
const SUB = L.sub;
const CLOSE_LABEL = L.close;
const DONE_LABEL = L.done;
const PAGE_LABEL = L.page;
const LABEL_LOADING = L.loading;
const LABEL_ERROR = L.error;
const LABEL_SERVICES = L.services;
const LABEL_PRICING = L.pricing;
const LABEL_WHY = L.why;

const node = shellNode;

// --- stavba obsahu ----------------------------------------------------------

// Leadová věta stránky (tučně) + perex — pás v řeči pásu expedice modalu dopravy
function buildLead(lead) {
  if (!lead || (!lead.lead && !lead.rest)) return null;
  const box = node('p', 'bs-serv__lead');
  box.appendChild(icon('rosette', 'bs-serv__leadicon'));
  const txt = node('span', 'bs-serv__leadtxt');
  if (lead.lead) txt.appendChild(node('b', null, lead.lead));
  if (lead.rest) txt.appendChild(document.createTextNode((lead.lead ? ' ' : '') + lead.rest));
  box.appendChild(txt);
  return box;
}

// „Servis všech značek" + specializace jako wordmark chipy (stránka je kreslí
// jako pás wordmarků, tady je na to místo jen v řádku)
function buildBrands(claim, brands) {
  if (!claim && !brands.length) return null;
  const box = node('div', 'bs-serv__brands');
  if (claim) {
    const c = node('span', 'bs-serv__claim');
    c.appendChild(icon('check'));
    // z administrace přichází fragment věty, tedy malým písmenem
    c.appendChild(node('span', null, claim.charAt(0).toUpperCase() + claim.slice(1)));
    box.appendChild(c);
  }
  if (brands.length) {
    box.appendChild(node('span', 'bs-serv__spec', LABEL_SPEC));
    const list = node('span', 'bs-serv__marks');
    brands.forEach(function (name) {
      list.appendChild(node('span', 'bs-serv__mark', name));
    });
    box.appendChild(list);
  }
  return box;
}

function buildCard(key, iconName, title) {
  const sec = node('section', 'bs-serv__card');
  sec.dataset.bsServ = key;
  const head = node('header', 'bs-serv__cardhead');
  const badge = node('span', 'bs-serv__cardicon');
  badge.appendChild(icon(iconName));
  head.appendChild(badge);
  head.appendChild(node('strong', 'bs-serv__cardtitle', title));
  sec.appendChild(head);
  return sec;
}

// Co nabízíme — položky s ikonou; drobné úkony nesou cenový chip (70 Kč /
// Zdarma) přímo z textu, položka s odkazem (Rady a tipy) je odkaz.
function buildServices(section) {
  if (!section.items.length) return null;
  const sec = buildCard('services', 'wrench', LABEL_SERVICES);
  const list = node('ul', 'bs-serv__list');
  section.items.forEach(function (item) {
    const li = node('li', 'bs-serv__item');
    li.appendChild(icon(item.icon, 'bs-serv__itemicon'));
    const txt = node('span', 'bs-serv__itemtxt');
    const name = item.href
      ? node('a', 'bs-serv__itemname bs-serv__itemlink', item.title)
      : node('span', 'bs-serv__itemname', item.title);
    if (item.href) name.href = item.href;
    txt.appendChild(name);
    if (item.desc) txt.appendChild(node('span', 'bs-serv__itemdesc', item.desc));
    li.appendChild(txt);
    if (item.chip) {
      li.appendChild(node('span', 'bs-serv__chip' + (L.freeRe.test(item.chip) ? ' is-free' : ''), item.chip));
    }
    list.appendChild(li);
  });
  sec.appendChild(list);
  return sec;
}

// Orientační ceník — název | cena (malé „od"); poznámka „(v případě výměny = cena výměny)"
// se u všech tří položek opakuje, proto jednou pod seznamem (stejné znění
// jako perex ceníku na stránce)
function buildPricing(section) {
  if (!section.items.length) return null;
  const sec = buildCard('pricing', 'tag', LABEL_PRICING);
  const list = node('ul', 'bs-serv__prices');
  let noted = false;
  section.items.forEach(function (item) {
    const li = node('li', 'bs-serv__price');
    li.appendChild(node('span', 'bs-serv__pricename', item.title));
    const val = node('strong', 'bs-serv__priceval');
    const parts = { prefix: item.priceFrom, value: item.priceAmount };
    if (parts.prefix) {
      val.appendChild(node('small', 'bs-serv__pricefrom', parts.prefix));
      val.appendChild(document.createTextNode(' '));
    }
    val.appendChild(node('span', null, parts.value));
    li.appendChild(val);
    list.appendChild(li);
    if (item.note) noted = true;
  });
  sec.appendChild(list);
  if (noted) sec.appendChild(node('p', 'bs-serv__pricenote', LABEL_PRICE_PEREX));
  return sec;
}

function buildWhy(section) {
  if (!section.items.length) return null;
  const sec = buildCard('why', 'shield', LABEL_WHY);
  const list = node('ul', 'bs-serv__why');
  section.items.forEach(function (item) {
    const li = node('li', 'bs-serv__whyitem');
    li.appendChild(icon(item.icon, 'bs-serv__whyicon'));
    const txt = node('span', 'bs-serv__whytxt');
    txt.appendChild(node('b', null, item.title));
    if (item.desc) txt.appendChild(document.createTextNode(' ' + item.desc));
    li.appendChild(txt);
    list.appendChild(li);
  });
  sec.appendChild(list);
  return sec;
}

// Kontakt na servis — znění pásu CTA ze stránky servisu, telefon z CONTACT
// (jediný zdroj kontaktů na webu)
function buildContact() {
  const box = node('div', 'bs-serv__contact');
  const badge = node('span', 'bs-serv__contacticon');
  badge.appendChild(icon('headset'));
  box.appendChild(badge);
  const txt = node('span', 'bs-serv__contacttxt');
  txt.appendChild(node('strong', null, LABEL_BAND_TITLE));
  txt.appendChild(node('span', null, LABEL_BAND_TEXT));
  box.appendChild(txt);
  // tlačítko: chat → e-mail → telefon (reachContact); s chatem e-mail pod textem
  const reach = reachContact();
  if (reach.chat) {
    const mail = node('a', 'bs-serv__mail', CONTACT.email);
    mail.href = 'mailto:' + CONTACT.email;
    txt.appendChild(mail);
  }
  const tel = node('a', 'bs-serv__tel');
  tel.href = reach.href;
  if (reach.chat) asChatTrigger(tel, 'service-modal');
  tel.appendChild(icon(reach.icon));
  tel.appendChild(node('span', null, reach.text));
  box.appendChild(tel);
  return box;
}

function fill(ui, data) {
  ui.body.textContent = '';
  // výpadek stránky = hláška, odkaz na celou stránku nese patička
  if (!data) {
    ui.body.appendChild(node('p', shellClass(PREFIX, 'msg'), LABEL_ERROR));
    return;
  }

  const lead = buildLead(data.lead);
  if (lead) ui.body.appendChild(lead);
  const brands = buildBrands(data.claim.text, data.brands.names);
  if (brands) ui.body.appendChild(brands);

  // desktop: co nabízíme vlevo (7 řádků), ceník + proč vpravo; mobil pod sebou
  const cols = node('div', 'bs-serv__cols');
  const services = buildServices(data.services);
  if (services) cols.appendChild(services);
  const side = node('div', 'bs-serv__side');
  const pricing = buildPricing(data.pricing);
  if (pricing) side.appendChild(pricing);
  const why = buildWhy(data.why);
  if (why) side.appendChild(why);
  if (side.childElementCount) cols.appendChild(side);
  if (cols.childElementCount) ui.body.appendChild(cols);

  ui.body.appendChild(buildContact());
}

// --- napojení ---------------------------------------------------------------

export function initServiceModal() {
  // idempotence: modal visí na dokumentu, ne na uzlu detailu
  if (document.documentElement.dataset.bsServ) return;
  document.documentElement.dataset.bsServ = '1';

  const modal = createModal({
    prefix: PREFIX,
    icon: 'wrench',
    title: TITLE,
    sub: SUB,
    closeLabel: CLOSE_LABEL,
    doneLabel: DONE_LABEL,
    link: { label: PAGE_LABEL, href: serviceHref },
  });

  function open(href) {
    const ui = modal.open();
    if (!ui) return;
    ui.body.appendChild(node('p', shellClass(PREFIX, 'msg'), LABEL_LOADING));
    loadServicePage(href).then(function (data) {
      if (modal.isOpen()) fill(ui, data);
    });
  }

  document.addEventListener(
    'click',
    function (e) {
      const target = e.target instanceof Element ? e.target.closest('a[data-bs-service]') : null;
      if (!target) return;
      // na file:// (offline fixtures) i bez fetch necháváme nativní navigaci,
      // klik s modifikátorem = zákazník chce stránku v novém okně
      if (!window.fetch || location.protocol === 'file:') return;
      if (e.metaKey || e.ctrlKey || e.shiftKey || e.altKey) return;
      e.preventDefault();
      e.stopPropagation();
      open(target.getAttribute('href'));
    },
    true
  );
}
